/**
 * Auto-zoom suggestion engine + runtime zoom computation.
 * Ported from Recordly's zoomSuggestionUtils + zoomRegionUtils.
 *
 * Three detection phases:
 *   1. Explicit clicks  → zoom in at click position (double-click detection included)
 *   2. Dwell events     → zoom in at resting position
 *   3. Strength-based conflict resolution with min gap enforcement
 *
 * Runtime: computeZoomAtTime() implements Recordly's exact timing model:
 *   - Lead-in 400ms before region start
 *   - Full zoom 500ms after region start
 *   - Lead-out 400ms after region end
 *   - Connected pan between adjacent regions < 1500ms apart
 */

import { type ZoomRegion } from '@/store/editor-store'
import { type MouseEvent as TrackedMouseEvent } from '@/store/recording-store'

// ── Constants (from Recordly / Cursorful tuning) ───────────────────────────────

const ZOOM_IN_TRANSITION_MS = 1200   // 1.2s smooth gliding lead-in
const ZOOM_OUT_MS           = 1100   // 1.1s smooth gliding lead-out

const MIN_GAP_MS            = 3000  // Minimum gap between suggestions
const CLICK_ZOOM_DURATION_MS = 3400  // 1200ms in + 1100ms hold + 1100ms out
const DWELL_ZOOM_DURATION_MS = 3000  // 1200ms in + 700ms hold + 1100ms out

// Double-click detection
const DBLCLICK_TIME_MS  = 350   // Max time between two clicks to be a double-click
const DBLCLICK_DIST_PCT = 0.04  // Max normalized distance (4% of screen)

// Strength scores (higher = wins conflict resolution)
const STRENGTH_DBLCLICK    = 1500
const STRENGTH_DROPDOWN    = 1200
const STRENGTH_TEXT_SELECT = 1300
const STRENGTH_TEXT_FIELD  = 1100
const STRENGTH_CLICK       = 900
const STRENGTH_DWELL_LONG  = 800   // dwell ≥ 1100ms equivalent
const STRENGTH_DWELL_SHORT = 600

// Depth → scale multipliers (Recordly's table)
export const DEPTH_SCALE: Record<number, number> = {
    1: 1.25,
    2: 1.75,
    3: 2.50,
    4: 3.50,
    5: 5.00,
}

// ── Math helpers ───────────────────────────────────────────────────────────────

function clamp01(v: number) { return v < 0 ? 0 : v > 1 ? 1 : v }

/** Ultra-smooth Quintic Ease-In-Out transition (takes time, zero jerk at start/end) */
export function easeInOutQuint(t: number): number {
    return t < 0.5
        ? 16 * Math.pow(t, 5)
        : 1 - Math.pow(-2 * t + 2, 5) / 2
}

/** Smooth Ease-Out Quintic transition */
export function easeOutQuint(t: number): number {
    return 1 - Math.pow(1 - t, 5)
}

/** Get easing curve value based on EaseType selection */
export function getEasingValue(t: number, easeType?: string): number {
    const clamped = clamp01(t)
    switch (easeType) {
        case 'linear':
            return clamped
        case 'easeOut':
            return easeOutQuint(clamped)
        case 'easeInOut':
        default:
            return easeInOutQuint(clamped)
    }
}

// Default Screen Studio / Cursorful ease
export const easeScreenStudio = (t: number) => easeInOutQuint(clamp01(t))

// ── Coordinate conversion ─────────────────────────────────────────────────────

/** Convert pixel position to ZoomRegion x/y (range -50..+50) */
function toZoomXY(px: number, py: number, sw: number, sh: number) {
    return {
        x: (px / sw - 0.5) * 100,
        y: (py / sh - 0.5) * 100,
    }
}

/** Normalized distance between two points (0-1 space) */
function normalizedDist(ax: number, ay: number, bx: number, by: number, sw: number, sh: number) {
    const dx = (ax - bx) / sw
    const dy = (ay - by) / sh
    return Math.sqrt(dx * dx + dy * dy)
}

// ── Interaction candidate ─────────────────────────────────────────────────────

interface Candidate {
    t: number       // center time in ms (used for startTime placement)
    x: number       // ZoomRegion x (-50..+50)
    y: number       // ZoomRegion y (-50..+50)
    scale: number   // zoom scale
    strength: number
    duration: number // ms
}

// ── Dwell detection from move trajectories ────────────────────────────────────

const MIN_DWELL_DETECT_MS  = 450   // shortest dwell to detect from moves
const MAX_DWELL_DETECT_MS  = 2600  // longest dwell (longer = scroll/reading, skip)

function detectDwellsFromMoves(
    moves: TrackedMouseEvent[],
    existingCandidates: Candidate[],
    sw: number,
    sh: number,
): Candidate[] {
    if (moves.length < 2) return []
    const results: Candidate[] = []

    let anchorIdx = 0
    const DWELL_BOUNDING_BOX_PCT = 0.03

    const commitRun = (endIdx: number) => {
        const dur = moves[endIdx].t - moves[anchorIdx].t
        if (dur < MIN_DWELL_DETECT_MS || dur > MAX_DWELL_DETECT_MS) return
        const centerT = (moves[anchorIdx].t + moves[endIdx].t) / 2
        
        // Skip if too close to a click candidate
        if (existingCandidates.some(c => Math.abs(c.t - centerT) < 600)) return
        
        // Calculate average position during the dwell
        let sumX = 0, sumY = 0
        for(let i = anchorIdx; i <= endIdx; i++) {
            sumX += moves[i].x
            sumY += moves[i].y
        }
        const runLen = endIdx - anchorIdx + 1
        const avgX = sumX / runLen
        const avgY = sumY / runLen
        const { x, y } = toZoomXY(avgX, avgY, sw, sh)
        
        results.push({
            t: centerT,
            x, y,
            scale: DEPTH_SCALE[2],  // 1.75x for deeper Cursorful style dwells
            strength: Math.min(dur, STRENGTH_DWELL_LONG),
            duration: DWELL_ZOOM_DURATION_MS,
        })
    }

    for (let i = 1; i < moves.length; i++) {
        const dist = Math.hypot(
            (moves[i].x - moves[anchorIdx].x) / sw,
            (moves[i].y - moves[anchorIdx].y) / sh,
        )
        // If cursor escapes the anchor bounding box, commit and reset anchor
        if (dist >= DWELL_BOUNDING_BOX_PCT) {
            commitRun(i - 1)
            anchorIdx = i
        }
    }
    commitRun(moves.length - 1)
    return results
}

// ── Suggestion generation ─────────────────────────────────────────────────────

export function generateZoomSuggestions(
    events: TrackedMouseEvent[],
    screenWidth: number,
    screenHeight: number,
    videoDurationMs?: number,
): ZoomRegion[] {
    if (!events || events.length === 0) return []

    const sw = screenWidth  || 1920
    const sh = screenHeight || 1080

    const clicks        = events.filter(e => e.type === 'click')
    const moves         = events.filter(e => e.type === 'move')
    const explicitDwells = events.filter(e => e.type === 'dwell')

    const candidates: Candidate[] = []

    // ── Phase 1: Click events ─────────────────────────────────────────────────
    for (let i = 0; i < clicks.length; i++) {
        const c = clicks[i]

        // Double-click detection: two clicks within DBLCLICK_TIME_MS and DBLCLICK_DIST_PCT
        const prev = clicks[i - 1]
        if (prev && (c.t - prev.t) <= DBLCLICK_TIME_MS
            && normalizedDist(c.x, c.y, prev.x, prev.y, sw, sh) <= DBLCLICK_DIST_PCT) {
            // Replace the previous single-click candidate with a double-click
            const last = candidates[candidates.length - 1]
            if (last && Math.abs(last.t - prev.t) < 100) candidates.pop()
            const { x, y } = toZoomXY(c.x, c.y, sw, sh)
            candidates.push({
                t: (prev.t + c.t) / 2,  // center of the two clicks
                x, y,
                scale: DEPTH_SCALE[3],  // 2.5x for double-click
                strength: STRENGTH_DBLCLICK,
                duration: CLICK_ZOOM_DURATION_MS,
            })
            i++ // skip second click of the pair
            continue
        }

        // Post-click behavior analysis: 400ms–2000ms AFTER click (Recordly window)
        // Starting at 400ms gives time for the user to reveal their intent.
        const postMoves = moves.filter(m => m.t >= c.t + 400 && m.t <= c.t + 2000)

        let strength = STRENGTH_CLICK
        let scale    = DEPTH_SCALE[2]  // 1.75x default (was 2.5x — more natural)

        if (postMoves.length < 2) {
            // No meaningful movement → text field / precise button click
            strength = STRENGTH_TEXT_FIELD
            scale    = DEPTH_SCALE[1]  // 1.25x — gentle zoom for text fields
        } else {
            const first  = postMoves[0]
            const last   = postMoves[postMoves.length - 1]
            const totalDx = (last.x - first.x) / sw
            const totalDy = (last.y - first.y) / sh
            const absDx  = Math.abs(totalDx)
            const absDy  = Math.abs(totalDy)

            if (absDx > 0.03 && absDx > absDy * 1.8) {
                // Horizontal drag → text selection (lower interest — small zoom)
                strength = STRENGTH_TEXT_SELECT
                scale    = DEPTH_SCALE[1]  // 1.25x
            } else if (totalDy > 0.03 && absDy > absDx * 1.5) {
                // Downward movement → dropdown / menu open
                strength = STRENGTH_DROPDOWN
                scale    = DEPTH_SCALE[2]  // 1.75x
            } else {
                // Check max distance from click point
                const maxDist = postMoves.reduce((m, mv) =>
                    Math.max(m, normalizedDist(mv.x, mv.y, c.x, c.y, sw, sh)), 0)
                strength = maxDist < 0.02 ? STRENGTH_TEXT_FIELD : STRENGTH_CLICK
                scale    = maxDist < 0.02 ? DEPTH_SCALE[1] : DEPTH_SCALE[2]
            }
        }

        const { x, y } = toZoomXY(c.x, c.y, sw, sh)
        candidates.push({ t: c.t, x, y, scale, strength, duration: CLICK_ZOOM_DURATION_MS })
    }

    // ── Phase 2: Dwell detection from move trajectories ───────────────────────
    // Detect sustained pauses (450–2600ms) by analyzing cursor movement runs.
    const moveDwells = detectDwellsFromMoves(moves, candidates, sw, sh)
    candidates.push(...moveDwells)

    // ── Phase 3: Explicit pre-classified dwell events ─────────────────────────
    for (const e of explicitDwells) {
        if (candidates.some(c => Math.abs(c.t - e.t) < 600)) continue
        const { x, y } = toZoomXY(e.x, e.y, sw, sh)
        candidates.push({
            t: e.t,
            x, y,
            scale: DEPTH_SCALE[2],
            strength: STRENGTH_DWELL_SHORT,
            duration: DWELL_ZOOM_DURATION_MS,
        })
    }

    // ── Phase 4: Strength-based greedy placement (Recordly approach) ──────────
    // Sort ALL candidates by strength descending — strongest events placed first.
    // This ensures the most important moments are never displaced by weaker ones.
    candidates.sort((a, b) => b.strength - a.strength)

    const result: ZoomRegion[] = []
    const placedCenters: number[] = []

    for (const c of candidates) {
        // Skip events that start at or after the video ends
        if (videoDurationMs != null && c.t >= videoDurationMs) continue

        // Skip if too close (in time) to any already-placed zoom center
        if (placedCenters.some(pc => Math.abs(pc - c.t) < MIN_GAP_MS)) continue

        // Center the zoom region on the event (not pre-roll from the start)
        const startMs = Math.max(0, c.t - c.duration / 2)
        const endMs   = videoDurationMs != null
            ? Math.min(startMs + c.duration, videoDurationMs)
            : startMs + c.duration

        result.push({
            id: crypto.randomUUID(),
            startTime: startMs / 1000,
            endTime:   endMs   / 1000,
            scale: c.scale,
            x: c.x,
            y: c.y,
            ease: 'easeInOut',
            mode: 'focus',
        })
        placedCenters.push(c.t)
    }

    // Re-sort by startTime so the timeline renders in order
    result.sort((a, b) => a.startTime - b.startTime)
    return result
}

// ── Runtime zoom computation ───────────────────────────────────────────────────

/**
 * Compute zoom transform at a given time using Recordly's timing model.
 * Returns { scale, x, y } in the same format as ZoomRegion (x/y in -50..+50 range).
 *
 * Timing model:
 *   [leadInStart]──easeIn──[fullZoomStart]──full──[endMs]──easeOut──[leadOutEnd]
 *   leadInStart  = startMs - 400ms
 *   fullZoomStart = startMs + 500ms
 *   leadOutEnd   = endMs + 400ms
 *
 * Connected pan: if two regions are < 1500ms apart, instead of fading to 1x
 * and back, the camera smoothly pans from one focus to the next.
 */
/**
 * Clamp translate so the video never goes outside the canvas bounds.
 * At scale S, max translate is ±50*(S-1)/S percent of the element's size.
 */
function clampTranslate(x: number, y: number, scale: number): { x: number; y: number } {
    if (scale <= 1) return { x: 0, y: 0 }
    const maxT = 50 * (scale - 1) / scale
    return {
        x: Math.max(-maxT, Math.min(maxT, x)),
        y: Math.max(-maxT, Math.min(maxT, y)),
    }
}

export function computeZoomAtTime(
    timeSec: number,
    regions: ZoomRegion[],
    cursorPos?: { x: number; y: number }
): { scale: number; x: number; y: number } {
    if (!regions || regions.length === 0) return { scale: 1, x: 0, y: 0 }

    const timeMs = timeSec * 1000

    for (let i = 0; i < regions.length; i++) {
        const r = regions[i]
        const startMs       = r.startTime * 1000
        const endMs         = r.endTime   * 1000
        // Transitions are contained within [startMs, endMs] so the timeline bar
        // accurately shows when the zoom effect is active.
        const fullZoomStart = startMs + ZOOM_IN_TRANSITION_MS
        const leadOutStart  = endMs   - ZOOM_OUT_MS

        if (timeMs < startMs) continue
        if (timeMs > endMs) continue

        const multiplier = r.mode === 'pan' ? 1 : -1
        let strength: number

        if (timeMs < fullZoomStart) {
            // Lead-in phase: 0 → 1 over ZOOM_IN_TRANSITION_MS
            const progress = (timeMs - startMs) / ZOOM_IN_TRANSITION_MS
            strength = getEasingValue(progress, r.ease)
        } else if (timeMs < leadOutStart) {
            strength = 1
        } else {
            // Lead-out phase: 1 → 0 over ZOOM_OUT_MS
            const progress = (timeMs - leadOutStart) / ZOOM_OUT_MS
            strength = 1 - getEasingValue(progress, r.ease)
        }

        const scale = 1 + (r.scale - 1) * strength
        
        let targetX = r.x
        let targetY = r.y
        
        // Live Camera Cursor Tracking:
        // Ensures the camera stays centered on the live cursor position so the mouse
        // never escapes outside the zoomed viewport during playback.
        if (cursorPos && strength > 0) {
            const cursorZoomX = (cursorPos.x - 0.5) * 100
            const cursorZoomY = (cursorPos.y - 0.5) * 100
            
            // Smoothly shift target from initial region anchor (r.x, r.y) to live cursor position
            targetX = r.x * (1 - strength) + cursorZoomX * strength
            targetY = r.y * (1 - strength) + cursorZoomY * strength
        }

        const rawX  = targetX * multiplier * strength
        const rawY  = targetY * multiplier * strength
        return { scale, ...clampTranslate(rawX, rawY, scale) }
    }

    return { scale: 1, x: 0, y: 0 }
}
